import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "sonner";
import {
  getLocalStorage,
  removeLocalStorage,
  setLocalStorage,
} from "../../util/manageStorage";
import { LoginRequest, RegisterRequest, RegisterResponse, User } from "../../types/authTypes";
import userService, { getApiError } from "./UserService";
import { setAuthUser } from "../../util/manageAuth";

/* ===================== STATE ===================== */

interface UserState {
  user: User | null;
  isLoading: boolean;
}

const initialState: UserState = {
  user: getLocalStorage("authUser") || null,
  isLoading: false,
};

/* ===================== THUNKS ===================== */

export const passwordLogin = createAsyncThunk<
  User,
  LoginRequest,
  { rejectValue: string }
>("user/passwordLogin", async (payload, thunkAPI) => {
  try {
    const response = await userService.passwordLogin(payload);
    const userData = response.data;

    setAuthUser(userData);

    return userData;
  } catch (error: any) {
    if (error.response?.status === 401) {
      return thunkAPI.rejectWithValue("Email or Password is incorrect");
    }

    return thunkAPI.rejectWithValue(
      error.response?.data?.message || "Login failed"
    );
  }
});

export const registerUser = createAsyncThunk<
  RegisterResponse,
  RegisterRequest,
  { rejectValue: string }
>("user/register", async (payload, thunkAPI) => {
  try {
    return await userService.register(payload);
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response
        ? getApiError(error.response.data, "Registration failed")
        : error.message || "Registration failed"
    );
  }
});

/* ===================== SLICE ===================== */

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    /** The user chose their own password (their account was made with a temporary one). */
    passwordChanged: (state) => {
      if (!state.user) return;
      state.user = { ...state.user, mustChangePassword: false };
      setLocalStorage("authUser", state.user);
    },
    logout: (state, action) => {
      state.user = null;

      removeLocalStorage("authUser");

      toast.dismiss();
      toast.warning(action.payload || "Logout successful");
    },
  },
  extraReducers: (builder) => {
    builder
      /* ---------- Login ---------- */
      .addCase(passwordLogin.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(passwordLogin.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isLoading = false;

        setLocalStorage("authUser", action.payload);

        toast.dismiss();
        toast.success("Welcome, " + action.payload.userName);
      })
      .addCase(passwordLogin.rejected, (state, action) => {
        state.isLoading = false;
        toast.error(action.payload);
      })

      /* ---------- Register ---------- */
      .addCase(registerUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(registerUser.fulfilled, (state) => {
        state.isLoading = false;

        toast.dismiss();
        toast.success("Account created! Please sign in.");
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isLoading = false;

        toast.dismiss();
        toast.error(action.payload || "Registration failed");
      });
  },
});

export const { logout, passwordChanged } = userSlice.actions;
export default userSlice.reducer;
